import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, TriangleAlert, Zap, QrCode, Smartphone, CreditCard, CheckCircle2,
  ExternalLink, Satellite, ShieldCheck,
} from "lucide-react";
import { ASSETS, APP_LINKS } from "@/data/products";
import SEO from "@/components/SEO";
import AppBadgeLink from "@/components/AppBadgeLink";

const STEPS = [
  { icon: <CreditCard size={20} />, title: "Buy an eSIM", text: "Open the PTT NZ app and purchase a multi-network eSIM — no store visit, no waiting for a physical SIM to arrive." },
  { icon: <Smartphone size={20} />, title: "One-tap install", text: "Install the eSIM straight onto your phone with a single tap, right from the app." },
  { icon: <QrCode size={20} />, title: "Or scan a QR code", text: "Setting up another device — a router, tablet or a colleague's phone? Generate a QR code or manual code and activate it there instead." },
  { icon: <Zap size={20} />, title: "Connected in ~60 seconds", text: "You're online across Spark NZ, One NZ and 2degrees — strongest signal wins, with automatic failover if any one network drops." },
];

const FAQS = [
  {
    q: "Why is my mobile network down right now?",
    a: "Outages happen to every carrier — tower faults, fibre cuts to a cell site, severe weather, planned maintenance or regional congestion can all take a single network offline for anywhere from minutes to hours. If you're only on one carrier, there's nothing you can do but wait it out.",
  },
  {
    q: "What's the fastest way to get connected during a network outage in New Zealand?",
    a: "The fastest option is an eSIM you can activate immediately from your phone. PTT NZ's app lets you buy and install a multi-network eSIM in about 60 seconds — no store visit, no waiting for a courier — connecting you to whichever of Spark NZ, One NZ or 2degrees currently has the strongest working signal.",
  },
  {
    q: "Does PTT NZ still work if Spark, One NZ or 2degrees has an outage?",
    a: "Yes. Every PTT NZ SIM and eSIM continuously monitors real data access, not just signal bars, across all three New Zealand networks. If the network you're on stops passing data — outage, disaster or fault — your device automatically switches to the next available network with no manual intervention.",
  },
  {
    q: "Can I get an eSIM without visiting a store?",
    a: "Yes. The entire process — purchase, activation and setup — happens inside the PTT NZ app. You never need to visit a store or wait for a physical SIM card to be shipped.",
  },
  {
    q: "Is this just for emergencies, or can I keep using it afterwards?",
    a: "Both. Plenty of customers activate a PTT NZ eSIM specifically during an outage and then simply keep using it as their everyday multi-network SIM afterwards, since it removes the single-carrier risk going forward.",
  },
];

const NetworkOutage = () => (
  <div data-testid="network-outage-page">
    <SEO
      path="/network-outage"
      title="Network Down? Get Connected in 60 Seconds"
      description="Spark, One NZ or 2degrees down? Activate a PTT NZ multi-network eSIM in about 60 seconds — automatic failover across all three NZ networks."
      keywords="Spark down alternative, One NZ outage alternative, 2degrees not working, mobile network outage New Zealand, backup SIM network outage NZ, emergency eSIM New Zealand, instant eSIM activation NZ, network down alternative SIM, network outage backup connectivity NZ"
      jsonLd={{
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
      }}
    />

    {/* HERO */}
    <section className="relative overflow-hidden bg-[var(--ptt-ink)] text-white">
      <div className="absolute inset-0 hairline-grid-dark opacity-60" />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-10 pt-16 lg:pt-24 pb-16 lg:pb-24">
        <div className="inline-flex items-center gap-2.5 mb-6 px-3 py-1.5 border border-amber-400/40 bg-amber-400/10" data-testid="outage-alert-badge">
          <TriangleAlert size={15} className="text-amber-400" />
          <span className="font-mono-tech text-xs tracking-widest uppercase text-amber-300">Network down? Read this</span>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.02] tracking-tight max-w-3xl">
          Spark, One NZ or 2degrees down? <span className="text-[var(--ptt-teal)]">Get connected in about 60 seconds.</span>
        </h1>
        <p className="mt-6 text-base md:text-lg text-white/75 max-w-2xl leading-relaxed">
          A single-carrier outage shouldn&apos;t mean you're offline. PTT NZ eSIMs activate straight from your phone — no store visit, no waiting — and automatically fail over between all three New Zealand networks the moment one drops.
        </p>
        <div className="mt-9 flex flex-wrap gap-4" data-testid="outage-hero-app-badges">
          <AppBadgeLink href={APP_LINKS.appStore} badgeSrc={ASSETS.appStoreBadge} badgeAlt="Download on the App Store" storeName="the App Store" testId="outage-app-store-badge" className="inline-block transition-transform hover:scale-[1.04]" imgClassName="h-12 w-auto" />
          <AppBadgeLink href={APP_LINKS.googlePlay} badgeSrc={ASSETS.googlePlayBadge} badgeAlt="Get it on Google Play" storeName="Google Play" testId="outage-google-play-badge" className="inline-block transition-transform hover:scale-[1.04]" imgClassName="h-12 w-auto" />
        </div>
        <div className="mt-8 flex flex-wrap gap-3">
          <a href="https://alerts.pttnz.net" target="_blank" rel="noopener noreferrer" data-testid="outage-cta-alerts" className="ghost-button px-5 py-3 text-sm font-semibold inline-flex items-center gap-2 border border-white/30 hover:border-[var(--ptt-teal)] hover:text-[var(--ptt-teal)] transition-colors">Check live NZ road &amp; network alerts <ExternalLink size={13} /></a>
          <Link to="/iot-data-pools" data-testid="outage-cta-plans" className="text-sm font-semibold inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors">See all SIM &amp; eSIM plans <ArrowRight size={14} /></Link>
        </div>
      </div>
    </section>

    {/* 60-SECOND STEPS */}
    <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-20">
      <div className="eyebrow">60-second activation</div>
      <h2 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-[1.05] max-w-2xl">From no signal to online, in four steps.</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10" data-testid="outage-steps-grid">
        {STEPS.map((s, i) => (
          <div key={s.title} className="relative bg-white border border-[var(--ptt-line)] p-6" data-testid={`outage-step-${i}`}>
            <div className="font-mono-tech text-[11px] text-[var(--ptt-teal-deep)]">0{i + 1}</div>
            <div className="w-11 h-11 grid place-items-center bg-[var(--ptt-teal)]/15 mt-3">{s.icon}</div>
            <h3 className="font-display text-base font-bold mt-4">{s.title}</h3>
            <p className="text-sm text-[var(--ptt-muted)] mt-2 leading-relaxed">{s.text}</p>
          </div>
        ))}
      </div>
    </section>

    {/* AUTO-FAILOVER REASSURANCE */}
    <section className="bg-white border-y border-[var(--ptt-line)]">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-20 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <div className="eyebrow">Why it keeps working after today</div>
          <h2 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-[1.05]">One outage today. Zero single-carrier risk from now on.</h2>
          <p className="mt-4 text-[var(--ptt-ink-soft)] leading-relaxed max-w-xl">
            Every PTT NZ SIM and eSIM continuously monitors real data access — not just signal bars — across Spark NZ, One NZ and 2degrees. The moment data stops flowing on the network you're using, your device automatically switches to the next available one. No manual intervention, no dropped operations.
          </p>
          <Link to="/about#faq" data-testid="outage-learn-more-faq" className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold signal-underline">How the failover technology works <ArrowRight size={13} /></Link>
        </div>
        <div className="lg:col-span-6">
          <div className="border border-[var(--ptt-ink)] bg-[var(--ptt-paper)] p-6 lg:p-8">
            <div className="flex items-center gap-2.5 mb-4">
              <Satellite size={18} className="text-[var(--ptt-teal-deep)]" />
              <span className="font-mono-tech text-[11px] tracking-widest uppercase text-[var(--ptt-muted)]">Always-on, by design</span>
            </div>
            <ul className="space-y-3 text-sm text-[var(--ptt-ink-soft)]">
              {["All 3 NZ networks on one SIM — strongest signal wins", "Automatic failover, no settings to change", "Same eSIM keeps working across Australia too", "Manage everything from the PTT NZ app"].map((f) => (
                <li key={f} className="flex items-start gap-3"><CheckCircle2 size={15} className="text-[var(--ptt-teal-deep)] mt-0.5 shrink-0" />{f}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>

    {/* FAQ */}
    <section className="max-w-4xl mx-auto px-5 lg:px-10 py-16 lg:py-20" data-testid="outage-faq-section">
      <div className="eyebrow">FAQ</div>
      <h2 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-tight">Common questions during an outage.</h2>
      <div className="mt-8 space-y-6" data-testid="outage-faq-list">
        {FAQS.map((f, i) => (
          <div key={f.q} className="border-b border-[var(--ptt-line)] pb-6" data-testid={`outage-faq-${i}`}>
            <h3 className="font-display text-base font-bold">{f.q}</h3>
            <p className="text-[var(--ptt-muted)] mt-2 leading-relaxed">{f.a}</p>
          </div>
        ))}
      </div>
    </section>

    {/* FINAL CTA */}
    <section className="max-w-7xl mx-auto px-5 lg:px-10 mb-24">
      <div className="bg-[var(--ptt-ink)] text-white p-10 lg:p-14 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
        <div>
          <div className="eyebrow text-white/50 flex items-center gap-2"><ShieldCheck size={14} />Get ahead of the next one</div>
          <h3 className="font-display text-3xl lg:text-4xl font-bold mt-2">Activate a backup eSIM now — before you need it.</h3>
        </div>
        <Link to="/contact?interest=Custom%20Plan" data-testid="outage-final-cta-contact" className="ptt-button px-6 py-3.5 text-sm font-bold inline-flex items-center gap-2">Talk to us <ArrowRight size={16} /></Link>
      </div>
    </section>
  </div>
);

export default NetworkOutage;
